import { _isNotEmpty } from '@/littledash';
import type { SendCoverageGridRow } from '@/model/Agent.model';
import type { FC } from 'react';
import { definitionSourceText, tierText } from '@/components/Agents/SendValidator/utils/SendTiers';
import { formatCount } from '@/components/Agents/SendValidator/utils/SendText';
import SendTallyTable from './SendTallyTable';
import styles from './SendResults.module.scss';

/** A tier row as stored: one stored before tiers were split by source has no sources. */
interface StoredTierRow extends SendCoverageGridRow {
  by_definition_source?: Array<SendCoverageGridRow>;
}

interface SendTierGridProps {
  rows: ReadonlyArray<StoredTierRow>;
  /** The published rules the tiers divide: the grid's own denominator. */
  registerRules: number;
  /** Our data-quality checks, counted apart and never against the published rules. */
  tierEightChecks?: number;
  testId?: string;
}

/**
 * How the published rules landed for each tier, tier 0 to 7, out of the register's own total, each split by
 * where its rules' checks come from: CDISC's published encoding, our check derived from CDISC's prose, or no
 * possible check. So a derived rule is never counted as CDISC's own. Tier 8, data quality, is ours alone and
 * appears only in the line beneath, never against the published total. Every cell is the server's number.
 */
const SendTierGrid: FC<SendTierGridProps> = ({
  rows,
  registerRules,
  tierEightChecks,
  testId = 'invivo-send-validator__tier-grid',
}) => {
  if (!_isNotEmpty(rows)) {
    return null;
  }
  return (
    <div className={`${styles.gridScroll} mt3`} data-test-component="SendTierGrid" data-testid={testId}>
      <SendTallyTable
        caption={`Published rules by tier, out of ${formatCount(registerRules)}`}
        heading="Tier"
        cellPrefix="tier"
        testId="invivo-send-validator__tier-table"
        rows={rows.flatMap((row) => [
          { key: row.key, label: tierText(row.key), tally: row.tally, element: 'tier-row' },
          ...(row.by_definition_source ?? []).map((source) => ({
            key: `${row.key}-${source.key}`,
            label: definitionSourceText(source.key),
            tally: source.tally,
            element: 'tier-source-row',
            className: styles.tierSourceRow,
          })),
        ])}
      />
      {tierEightChecks !== undefined && (
        <p className="f6 dark-gray ma0 mt2" data-test-element="tier-eight">
          {tierText('tier_8')}: {formatCount(tierEightChecks)} of our own checks, counted apart and never against the{' '}
          {formatCount(registerRules)} published rules.
        </p>
      )}
    </div>
  );
};

export default SendTierGrid;
